import { clsx } from 'clsx';
import { ReactElement, useEffect, useState, ChangeEvent } from 'react';
import { TextInput } from '../TextInput';
import { TextInputProps } from '../TextInput/TextInput';

import styles from './currencyInput.module.scss';

export type CurrencyInputProps = TextInputProps & {
  className?: string;
  prefix?: string | ReactElement;
} & HTMLDataAttributes;

/**
 * CurrencyInput component
 */

function formatNumberWithNarrowNBSP(value: number | string, locale = 'nb-NO') {
  const digits = String(value).replace(/\D/g, '');
  const formatted = new Intl.NumberFormat(locale).format(+digits);

  // Replace normal or narrow spaces with narrow non-breaking space
  return formatted.replace(/\u00A0|\u2009/g, '\u202F');
}

export const CurrencyInput = ({
  className,
  prefix,
  value,
  onChange,
  ...props
}: CurrencyInputProps): ReactElement => {
  const [internalValue, setInternalValue] = useState(value ?? '');

  useEffect(() => {
    if (value !== undefined && value !== null) {
      const numberValue =
        typeof value === 'number'
          ? value
          : parseFloat(String(value).replace(/\D/g, '')) || 0;

      const formattedValue = formatNumberWithNarrowNBSP(numberValue);
      setInternalValue(formattedValue);
    }
  }, [value]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Keep only digits
    const digitsOnly = val.replace(/\D/g, '');

    setInternalValue(digitsOnly);

    const fakeEvent: ChangeEvent<HTMLInputElement> = {
      ...e,
      target: {
        ...e.target,
        value: digitsOnly,
      },
    };

    if (onChange) {
      onChange(fakeEvent);
    }
  };

  return (
    <TextInput
      {...props}
      className={clsx(styles.currencyInput, className)}
      prefix={<span className={styles.currencyPrefix}>{prefix}</span>}
      onChange={handleOnChange}
      value={formatNumberWithNarrowNBSP(internalValue)}
    />
  );
};
